// TUT-450 — a frota: veículos, viagens e anexos, locadoras e locações, política de transporte, abastecimento, referências de viagem, reservas e manutenção.
import { demoApi } from "../demo-data";
import { enviarAoArmazenamento, type RelatorDeProgresso } from "../upload-transport";
import type {
  Vehicle,
  CreateVehicleInput,
  UpdateVehicleInput,
  VehicleTrip,
  VehicleTripList,
  VehicleTripMode,
  CreateVehicleTripInput,
  UpdateVehicleTripInput,
  VehicleMaintenance,
  CreateVehicleMaintenanceInput,
  VehicleFueling,
  OrganizationTravelReference,
  SaveOrganizationTravelReferenceInput,
  CreateVehicleFuelingInput,
  VehicleReservation,
  CreateVehicleReservationInput,
  VehicleReservationCreateResult,
  RentalCompany,
  RentalCompanyListResponse,
  TransportPolicy,
  TripAttachment,
  TripAttachmentKind,
  CreateRentalCompanyInput,
  VehicleRental,
  OpenVehicleRentalInput,
  CloseVehicleRentalInput,
} from "../types";
import { ApiError } from "../api-error";
import { comFatia, request, sha256 } from "./infra";
import type { FatiaPedida } from "./infra";

// Lido aqui, e não importado: o empacotador só dobra a constante no módulo
// que a escreve, e é isso que tira o demo do build de produção (TUT-448).
const useDemoApi = import.meta.env.VITE_API_MODE === "demo";

export const apiDaFrota = {
  // Frota (TUT-129 fase 1).
  fleetVehicles(): Promise<{ items: Vehicle[] }> {
    if (useDemoApi) return demoApi.fleetVehicles();
    return request<{ items: Vehicle[] }>("/fleet/vehicles");
  },
  createVehicle(input: CreateVehicleInput): Promise<Vehicle> {
    if (useDemoApi) return demoApi.createVehicle(input);
    return request<Vehicle>("/fleet/vehicles", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  updateVehicle(id: string, input: UpdateVehicleInput): Promise<Vehicle> {
    if (useDemoApi) return demoApi.updateVehicle(id, input);
    return request<Vehicle>(`/fleet/vehicles/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    });
  },
  /** TUT-167 fatia 1 — "Devolver": encerra a locação e passa o carro a Inativo. */
  returnVehicle(id: string): Promise<Vehicle> {
    if (useDemoApi) return demoApi.returnVehicle(id);
    return request<Vehicle>(
      `/fleet/vehicles/${encodeURIComponent(id)}/return`,
      { method: "POST" },
    );
  },
  // TUT-191 — a lista agora pagina; sem page/pageSize a API devolve o
  // conjunto inteiro (mesmo contrato de `deadlines`/`comFatia` acima).
  fleetTrips(
    query: {
      vehicleId?: string;
      mode?: VehicleTripMode;
      search?: string;
    } & FatiaPedida = {},
  ): Promise<VehicleTripList> {
    if (useDemoApi) return demoApi.fleetTrips(query);
    const params = new URLSearchParams();
    if (query.vehicleId) params.set("vehicleId", query.vehicleId);
    if (query.mode) params.set("mode", query.mode);
    if (query.search) params.set("search", query.search);
    return request<VehicleTripList>(
      `/fleet/trips${comFatia(params, query)}`,
    );
  },
  createVehicleTrip(input: CreateVehicleTripInput): Promise<VehicleTrip> {
    if (useDemoApi) return demoApi.createVehicleTrip(input);
    return request<VehicleTrip>("/fleet/trips", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  updateVehicleTrip(
    id: string,
    input: UpdateVehicleTripInput,
  ): Promise<VehicleTrip> {
    if (useDemoApi) return demoApi.updateVehicleTrip(id, input);
    return request<VehicleTrip>(`/fleet/trips/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    });
  },
  // TUT-167 fatia 2 — locadoras e locações.
  rentalCompanies(
    includeInactive = false,
    fatia: FatiaPedida = {},
    search = "",
  ): Promise<RentalCompanyListResponse> {
    if (useDemoApi) return demoApi.rentalCompanies(includeInactive, fatia, search);
    const params = new URLSearchParams();
    if (includeInactive) params.set("includeInactive", "true");
    if (search) params.set("search", search);
    return request<RentalCompanyListResponse>(
      `/rental-companies${comFatia(params, fatia)}`,
    );
  },
  setRentalCompanyActive(
    id: string,
    active: boolean,
  ): Promise<RentalCompany> {
    if (useDemoApi) return demoApi.setRentalCompanyActive(id, active);
    return request<RentalCompany>(
      `/rental-companies/${encodeURIComponent(id)}/active`,
      { method: "POST", body: JSON.stringify({ active }) },
    );
  },
  deleteRentalCompany(id: string): Promise<void> {
    if (useDemoApi) return demoApi.deleteRentalCompany(id);
    return request<void>(`/rental-companies/${encodeURIComponent(id)}`, {
      method: "DELETE",
    });
  },
  createRentalCompany(input: CreateRentalCompanyInput): Promise<RentalCompany> {
    if (useDemoApi) return demoApi.createRentalCompany(input);
    return request<RentalCompany>("/rental-companies", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  updateRentalCompany(
    id: string,
    input: CreateRentalCompanyInput,
  ): Promise<RentalCompany> {
    if (useDemoApi) return demoApi.updateRentalCompany(id, input);
    return request<RentalCompany>(`/rental-companies/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    });
  },
  fleetRentals(): Promise<{ items: VehicleRental[] }> {
    if (useDemoApi) return demoApi.fleetRentals();
    return request<{ items: VehicleRental[] }>("/fleet/rentals");
  },
  openVehicleRental(input: OpenVehicleRentalInput): Promise<VehicleRental> {
    if (useDemoApi) return demoApi.openVehicleRental(input);
    return request<VehicleRental>("/fleet/rentals", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  closeVehicleRental(
    id: string,
    input: CloseVehicleRentalInput,
  ): Promise<VehicleRental> {
    if (useDemoApi) return demoApi.closeVehicleRental(id, input);
    return request<VehicleRental>(
      `/fleet/rentals/${encodeURIComponent(id)}/close`,
      { method: "POST", body: JSON.stringify(input) },
    );
  },
  // TUT-167 fatia 3 — a política chega RESOLVIDA, sob `frota.gerir`. Ler
  // `/tenant-settings` daqui não serviria: aquela lista é filtrada por
  // permissão e as chaves de transportes são de administrador, então a
  // configuração não valeria justamente para quem lança a viagem.
  transportPolicy(): Promise<TransportPolicy> {
    if (useDemoApi) return demoApi.transportPolicy();
    return request<TransportPolicy>("/fleet/transport-policy");
  },
  // Anexos da viagem. O fluxo é o MESMO de `uploadDocument`:
  // hash no navegador, autorização pela API, PUT direto no armazenamento e
  // conclusão pela API. A API nunca transmite os bytes — ela autoriza e audita.
  tripAttachments(tripId?: string): Promise<{ items: TripAttachment[] }> {
    if (useDemoApi) return demoApi.tripAttachments(tripId);
    const suffix = tripId ? `?tripId=${encodeURIComponent(tripId)}` : "";
    return request<{ items: TripAttachment[] }>(
      `/fleet/trips/attachments${suffix}`,
    );
  },
  async uploadTripAttachment(
    tripId: string,
    kind: TripAttachmentKind,
    file: File,
    aoProgredir?: RelatorDeProgresso,
  ): Promise<TripAttachment> {
    if (useDemoApi) {
      aoProgredir?.({ fase: "preparando" });
      const anexo = await demoApi.uploadTripAttachment(tripId, kind, file);
      aoProgredir?.({ fase: "concluindo" });
      return anexo;
    }
    aoProgredir?.({ fase: "preparando" });
    const checksumSha256 = await sha256(file);
    const prepared = await request<{
      uploadId: string;
      upload: { url: string; method: "PUT"; headers: Record<string, string> };
    }>(`/fleet/trips/${encodeURIComponent(tripId)}/attachments`, {
      method: "POST",
      body: JSON.stringify({
        kind,
        originalName: file.name,
        mimeType: file.type,
        sizeBytes: file.size,
        checksumSha256,
      }),
    });
    const uploadResponse = await enviarAoArmazenamento({
      url: prepared.upload.url,
      method: prepared.upload.method,
      headers: prepared.upload.headers,
      corpo: file,
      aoProgredir,
    });
    if (!uploadResponse.ok) {
      throw new ApiError(
        "O armazenamento recusou o arquivo. Tente novamente.",
        uploadResponse.status,
      );
    }
    aoProgredir?.({ fase: "concluindo" });
    return request<TripAttachment>(
      `/fleet/trips/attachments/${encodeURIComponent(prepared.uploadId)}/complete`,
      { method: "POST" },
    );
  },
  tripAttachmentViewLink(id: string): Promise<{ url: string }> {
    if (useDemoApi) return demoApi.tripAttachmentViewLink(id);
    return request<{ url: string }>(
      `/fleet/trips/attachments/${encodeURIComponent(id)}/view`,
      { method: "POST" },
    );
  },
  fleetFuelings(): Promise<{ items: VehicleFueling[] }> {
    if (useDemoApi) return demoApi.fleetFuelings();
    return request<{ items: VehicleFueling[] }>("/fleet/fuelings");
  },
  createVehicleFueling(
    input: CreateVehicleFuelingInput,
  ): Promise<VehicleFueling> {
    if (useDemoApi) return demoApi.createVehicleFueling(input);
    return request<VehicleFueling>("/fleet/fuelings", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  fleetTravelReferences(): Promise<{ items: OrganizationTravelReference[] }> {
    if (useDemoApi) return demoApi.fleetTravelReferences();
    return request<{ items: OrganizationTravelReference[] }>(
      "/fleet/travel-references",
    );
  },
  saveTravelReference(
    input: SaveOrganizationTravelReferenceInput,
  ): Promise<OrganizationTravelReference> {
    if (useDemoApi) return demoApi.saveTravelReference(input);
    // PUT porque a operação é idempotente por organização — o banco garante
    // uma linha por cliente, então repetir tem que dar o mesmo resultado.
    return request<OrganizationTravelReference>("/fleet/travel-references", {
      method: "PUT",
      body: JSON.stringify(input),
    });
  },
  fleetReservations(): Promise<{ items: VehicleReservation[] }> {
    if (useDemoApi) return demoApi.fleetReservations();
    return request<{ items: VehicleReservation[] }>("/fleet/reservations");
  },
  createVehicleReservation(
    input: CreateVehicleReservationInput,
  ): Promise<VehicleReservationCreateResult> {
    if (useDemoApi) return demoApi.createVehicleReservation(input);
    return request<VehicleReservationCreateResult>("/fleet/reservations", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  cancelVehicleReservation(id: string): Promise<VehicleReservation> {
    if (useDemoApi) return demoApi.cancelVehicleReservation(id);
    return request<VehicleReservation>(
      `/fleet/reservations/${encodeURIComponent(id)}/cancel`,
      { method: "POST" },
    );
  },
  fleetMaintenance(): Promise<{ items: VehicleMaintenance[] }> {
    if (useDemoApi) return demoApi.fleetMaintenance();
    return request<{ items: VehicleMaintenance[] }>("/fleet/maintenance");
  },
  createVehicleMaintenance(
    input: CreateVehicleMaintenanceInput,
  ): Promise<VehicleMaintenance> {
    if (useDemoApi) return demoApi.createVehicleMaintenance(input);
    return request<VehicleMaintenance>("/fleet/maintenance", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
};
